import { useState } from "react";
import { Button } from "../../../../../design/button";
import { Textarea } from "../../../../../design/textarea";
import { Filing } from "../../../../../models/Filing";
import { FilingService } from "../../../../../services/FilingService";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Input } from "../../../../../design/input";
import { Card } from "../../../../../design/card";
import { getPlainText } from "../../../../../utils/slate";
import { Descendant } from "slate";
import { createParagraphNode } from "../../../../../utils/nodeUtils";
import SlateTextArea from "../../../../../components/SlateTextArea/SlateTextArea";
import { FilingSection } from "../../../../../models/FilingSection";
import { useFiling } from "../../../../../contexts/FilingContext";

interface Props {
  filingId: Filing["_id"];
  sections: FilingSection[];
  insertAfterId: string | null;
}

const InlineInsertSection: React.FC<Props> = ({
  filingId,
  sections,
  insertAfterId,
}) => {
  const { createSection } = useFiling();
  const [showInput, setShowInput] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState<Descendant[]>([createParagraphNode()]);
  const [loading, setLoading] = useState(false);

  const handleAddSection = async () => {
    if (!content) return;
    setLoading(true);

    try {
      const sectionAfter = sections.find((s) => s._id === insertAfterId);
      const newIndex = sectionAfter
        ? sectionAfter.index + 1
        : Math.min(...sections.map((s) => s.index)) - 1;

      const newSectionId = await createSection(filingId, {
        title,
        content: JSON.stringify(content),
        description: getPlainText(content),
        index: newIndex,
        comments: [],
      });
      console.log("New section created with ID:", newSectionId);

      // Rebuild section order including new section's _id in the correct spot
      const updatedOrder = [];
      for (const section of sections) {
        updatedOrder.push(section._id);
        if (section._id === insertAfterId && newSectionId) {
          updatedOrder.push(newSectionId);
        }
      }

      // If inserting at the start or insertAfterId is null
      if (!insertAfterId && newSectionId) {
        updatedOrder.unshift(newSectionId);
      }

      // If insertAfterId was the last section
      if (
        insertAfterId &&
        !sections.some((s, i) => sections[i + 1]?._id === insertAfterId) &&
        newSectionId &&
        !updatedOrder.includes(newSectionId)
      ) {
        updatedOrder.push(newSectionId);
      }

      // Map to index objects
      const reordered = updatedOrder.map((id, i) => ({
        _id: id,
        index: (i + 1) * 10,
      }));

      await FilingService.reorderSections(
        filingId,
        reordered.map((section) => section._id as string),
      );

      setTitle("");
      setContent([createParagraphNode()]);
      setShowInput(false);
    } catch (err) {
      console.error("Failed to insert section", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 px-2 py-4 border-t border-dashed border-muted">
      {!showInput ? (
        <button
          onClick={() => setShowInput(true)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <PlusIcon className="w-4 h-4" />
          <span>Add Section</span>
        </button>
      ) : (
        <Card className="space-y-4">
          <Input
            placeholder="Section title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mb-2"
          />
          <SlateTextArea
            id={"section-content"}
            placeholder="Section content..."
            initialValue={content}
            onChange={(value) => setContent(value)}
          />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setShowInput(false)}>Cancel</Button>
            <Button onClick={handleAddSection} disabled={loading}>
              {loading ? "Saving..." : "Add Section"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default InlineInsertSection;
